import { useEffect, useState } from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { Check, Loader2 } from "lucide-react";
import { cn } from "@/utils/cn";
import { WizardCard } from "./visuals";

/**
 * While the agents are made. The phases are the real requests, in order:
 * reading the roteiro (one model call) and writing the agents into the flow.
 * A roteiro shows both; the questions only the second. No invented progress,
 * only the elapsed time. A spinner holds the place until the animation loads
 * (its player comes from a CDN), so the box is never empty.
 */

const PHASES = {
  reading: { title: "Lendo o seu roteiro", text: "Entendendo como o escritório atende e quais casos recebe." },
  building: { title: "Montando os agentes no fluxo", text: "Criando a recepção, a triagem e o fechamento no Touch Builder." },
};

export default function BuildingAgents({ phase, fromGuide }) {
  const [elapsed, setElapsed] = useState(0);
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const steps = fromGuide ? ["reading", "building"] : ["building"];
  const current = steps.indexOf(phase);

  return (
    <WizardCard as="section" role="status" aria-live="polite" aria-labelledby="building-title" className="mx-auto max-w-lg items-center p-8 text-center">
      <div className="relative h-36 w-36" aria-hidden="true">
        {!animated && <Loader2 data-testid="building-spinner" className="absolute inset-0 m-auto h-10 w-10 animate-spin text-muted-foreground" />}
        <DotLottieReact
          src="/assets/transcription.lottie"
          loop
          autoplay
          dotLottieRefCallback={(player) => player?.addEventListener("load", () => setAnimated(true))}
          style={{ width: "100%", height: "100%" }}
        />
      </div>
      <h2 id="building-title" className="mt-1 text-2xl font-semibold tracking-tight text-foreground">
        {PHASES[phase].title}
      </h2>
      <p className="mt-2 text-[15px] text-muted-foreground">{PHASES[phase].text}</p>

      {steps.length > 1 && (
        <ol className="mt-6 flex w-full max-w-xs flex-col gap-2.5 text-left">
          {steps.map((id, i) => (
            <li key={id} className={cn("flex items-center gap-3 text-sm", i > current ? "text-muted-foreground" : "text-foreground")}>
              <span
                aria-hidden="true"
                className={cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                  i < current
                    ? "bg-emerald-500 text-white"
                    : i === current
                      ? "bg-foreground text-background"
                      : "border border-black/[.12] dark:border-white/20",
                )}
              >
                {i < current ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : i + 1}
              </span>
              {PHASES[id].title}
            </li>
          ))}
        </ol>
      )}

      <p className="mt-6 text-xs font-medium tabular-nums text-muted-foreground">{elapsed}s</p>
    </WizardCard>
  );
}
